import { motion } from "framer-motion";
import { ArrowRight, Flame } from "lucide-react";
import { ABOUT } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];

export default function About() {
  return (
    <section
      id="about"
      className="relative overflow-hidden px-6 pb-16 pt-24 sm:px-10 lg:px-16 lg:pb-20 lg:pt-32"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-40 top-0 h-[500px] w-[500px] rounded-full bg-ember/15 blur-[140px]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-40 bottom-0 h-[500px] w-[500px] rounded-full bg-crimson/15 blur-[140px]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/3 h-[400px] w-[400px] -translate-x-1/2 rounded-full bg-gold/10 blur-[160px]"
      />

      <div className="relative grid gap-14 lg:grid-cols-2 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.35em] text-gold">
            {ABOUT.eyebrow}
          </p>
          <h2 className="bg-gradient-to-r from-amber-100 via-gold to-ember bg-clip-text font-serif text-2xl uppercase tracking-tight text-transparent sm:text-3xl lg:text-4xl">
            {ABOUT.heading}
          </h2>
          <div className="mt-6 max-w-xl space-y-5 text-base font-light leading-relaxed text-neutral-300 sm:text-lg">
            {ABOUT.paragraphs.map((p) => (
              <p key={p.slice(0, 24)}>{p}</p>
            ))}
          </div>

          <ul className="mt-10 grid gap-3 sm:grid-cols-2">
            {ABOUT.facts.map((fact, i) => (
              <motion.li
                key={fact.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: EASE }}
                className="group border border-amber-500/15 bg-[#141210] p-5 transition-all duration-500 hover:border-ember/50 hover:shadow-[0_0_40px_rgba(230,81,0,0.15)]"
              >
                <Flame className="size-4 text-ember" strokeWidth={1.5} />
                <p className="mt-3 font-serif text-lg text-amber-100">{fact.value}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                  {fact.label}
                </p>
              </motion.li>
            ))}
          </ul>

          <a
            href="#contact"
            data-testid="about-contact-cta"
            className="group mt-10 inline-flex items-center gap-3 border border-amber-500/30 px-7 py-4 font-mono text-xs uppercase tracking-[0.25em] text-amber-200 transition-all duration-300 hover:border-amber-400 hover:text-amber-50 hover:shadow-[0_0_40px_rgba(230,81,0,0.2)]"
          >
            Cook With Us
            <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          className="group relative self-start lg:sticky lg:top-28"
        >
          <div
            aria-hidden="true"
            className="absolute -inset-6 bg-gradient-to-tr from-ember/25 via-gold/15 to-crimson/20 opacity-60 blur-2xl transition-opacity duration-700 group-hover:opacity-100"
          />
          <img
            src={ABOUT.image}
            alt={ABOUT.imageAlt}
            loading="lazy"
            data-testid="about-image"
            className="relative aspect-[4/5] w-full border border-ember/30 object-cover brightness-[1.1] contrast-[1.08] saturate-[1.45] transition-transform duration-700 ease-out group-hover:scale-[1.02]"
          />
          <p className="relative mt-8 font-mono text-xs uppercase tracking-[0.3em] text-gold/70">
            {ABOUT.imageCaption}
          </p>
        </motion.div>
      </div>
    </section>
  );
}
